import React from 'react';
import { connect } from 'react-redux';
import { Motion, spring } from 'react-motion';
import { compose } from 'react-apollo';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import uuidv1 from 'uuid/v1';
import { Icon } from '@treelab/panda';

import { InputDropdown, Bar, BarTextInput, BarIcon, BarText } from '../../../../../panda';

import { GLYPHS } from '../../../../../constants/icons';

import { IDispatch } from '../../../../../modules/stores';
import { IFieldById } from '../../../../../types/core';
import { ISpace } from '../../../../../types/space';
import { IRefTableColumn } from '../../../../../types/space';
import { ElementTypes } from '../../../../../types/element';

import { InputTitle } from '../../../../Input';
import { TitleMixin, SmallerMarginMixin, DraggableItem, DraggableContainer } from './styled';
import { checkIfLookupIsLookingAtRecordReference } from '../../../../../modules/core/helpers';
import { ColumnType } from '../../../../../modules/core/graphql/types';

type IConnectProps = ReturnType<typeof mapDispatch>;
interface IProps extends IConnectProps, WrappedComponentProps {
  name: string;
  smallBottomMargin?: boolean;
  showRefAsText: boolean;
  fieldOptions: IFieldById[];
  selectedRefTableRecs: IRefTableColumn[];
  space: ISpace<ElementTypes>;
  title: string;
  suppressData?: boolean;
}
interface IState {
  topDeltaY: number;
  mouseY: number;
  isPressed: boolean;
  targetId: string;
}

/**
 * @param value should contain data saved in data config. Which references were selected.
 * @param view ViewModel to help us find field information
 */

const reinsert = (arr: any, from: any, to: any) => {
  const _arr = arr.slice(0);
  const val = _arr[from];
  _arr.splice(from, 1);
  _arr.splice(to, 0, val);
  return _arr;
};
const _clamp = (n: number, min: number, max: number) => Math.max(Math.min(n, max), min);
const springConfig = { stiffness: 500, damping: 26 };

class InputFieldSelection extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      topDeltaY: 0,
      mouseY: 0,
      isPressed: false,
      targetId: '',
    };
  }

  componentDidMount() {
    /** This is a fix that we have to run to make sure everything has their own uuid */
    if (this.props.selectedRefTableRecs.length && !this.props.selectedRefTableRecs[0].uid) {
      const updatedColumns = this.props.selectedRefTableRecs.map((rec) => ({
        ...rec,
        uid: uuidv1(),
      }));

      this.props.updateTableColumnEntirety({
        newColumns: updatedColumns,
        spaceId: this.props.space.id,
      });
    }

    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  }

  handleMouseDown = (id: string, pressY: number, { pageY }: { pageY: number }) => {
    this.setState({
      topDeltaY: pageY - pressY,
      mouseY: pressY,
      isPressed: true,
      targetId: id,
    });
  };

  handleMouseMove = ({ pageY }: { pageY: number }) => {
    const { isPressed, topDeltaY, targetId } = this.state;
    const { selectedRefTableRecs } = this.props;

    if (isPressed) {
      const mouseY = pageY - topDeltaY;
      const currentRowIdx = _clamp(Math.round(mouseY / 50), 0, selectedRefTableRecs.length - 1);
      let newColumns = null;

      const indexOfSelected = selectedRefTableRecs.findIndex((s) => s.uid === targetId);

      if (currentRowIdx !== indexOfSelected) {
        newColumns = reinsert(selectedRefTableRecs, indexOfSelected, currentRowIdx);
      }

      this.setState({ mouseY: mouseY });

      if (newColumns) {
        this.props.updateTableColumnEntirety({
          newColumns,
          spaceId: this.props.space.id,
        });
      }
    }
  };

  handleMouseUp = () => {
    if (this.state.isPressed) {
      this.setState({ isPressed: false, topDeltaY: 0 });
    }
  };

  public render() {
    const { mouseY, isPressed, targetId } = this.state;
    const {
      addTableColumnByType,
      smallBottomMargin,
      name,
      space,
      title,
      fieldOptions,
      selectedRefTableRecs,
      suppressData,
      intl: { formatMessage },
    } = this.props;

    return (
      <>
        {!!title && <InputTitle isSmall isReverse css={TitleMixin} title={title} />}
        <DraggableContainer numItems={selectedRefTableRecs.length}>
          {selectedRefTableRecs.map((selected, index: number) => {
            const style =
              targetId === selected.uid && isPressed
                ? { y: mouseY }
                : { y: spring(index * 50, springConfig) };

            return (
              <Motion style={style} key={index}>
                {({ y }) => (
                  <DraggableItem
                    style={{
                      transform: `translate3d(0, ${y}px, 0)`,
                      WebkitTransform: `translate3d(0, ${y}px, 0)`,
                      zIndex: targetId === selected.uid && isPressed ? 99 : index,
                    }}
                  >
                    {this.renderBar(index, selected, y)}
                  </DraggableItem>
                )}
              </Motion>
            );
          })}
        </DraggableContainer>
        <InputDropdown
          css={smallBottomMargin ? SmallerMarginMixin : ''}
          includeEmptyOption
          input={{
            value: '',
            name,
            onBlur: () => {},
            onChange: (e) => {
              addTableColumnByType({ colIdToAdd: e.target.value, spaceId: space.id, suppressData });
            },
            onFocus: () => {},
          }}
          isSmall
          isReverse
          meta={{}}
          emptyOptionTitle={formatMessage({ id: 'sidebar.columns.select' })}
          options={fieldOptions.map((f) => ({ name: f.name, value: f.id }))}
        />
      </>
    );
  }

  private renderBar = (index: number, selected: IRefTableColumn, y: number) => {
    const {
      updateTableColumnName,
      showRefAsText,
      removeTableColumn,
      addIndexDepth,
      addEditingAttachment,
      space,
      fieldOptions,
      selectedRefTableRecs,
      intl: { formatMessage },
    } = this.props;
    const field = fieldOptions.find((field) => field.id === selected.id);

    if (!field) {
      return (
        <Bar isSmall includeSpaceBelow isReverse key={`${index}-${selected.id}`}>
          <button
            className="panda-bar-item flex-center hover-cursor panda-bar-item-reverse panda-bar-item-button"
            onMouseDown={(e) => this.handleMouseDown(selected.uid, y, e)}
            type="button"
          >
            <Icon icon={GLYPHS.DRAG_INDICATOR} size={{ height: 16, width: 16 }} />
          </button>
          <BarText
            isReverse
            isSmall
            isFullWidth
            text={formatMessage({ id: 'sidebar.columns.error.missing' })}
          />
          {selectedRefTableRecs.length > 1 && (
            <BarIcon
              isSmall
              isReverse
              icon={GLYPHS.REMOVE}
              onClick={() => removeTableColumn({ spaceId: space.id, colIdIndex: index })}
              iconSize={{ height: 12, width: 12 }}
            />
          )}
        </Bar>
      );
    }

    const showDepthBtn =
      !showRefAsText &&
      (field.type === ColumnType.RECORD_REFERENCE ||
        field.type === ColumnType.SUBTABLE ||
        checkIfLookupIsLookingAtRecordReference(field));

    const attachmentId =
      field.type === ColumnType.MULTI_ATTACHMENT
        ? field.id
        : field.type === ColumnType.LOOKUP &&
          field.typeOptions.lookupTypeOptions.type === ColumnType.MULTI_ATTACHMENT
        ? field.typeOptions.foreignLookupColumnId
        : null;

    return (
      <Bar isSmall includeSpaceBelow isReverse key={`${index}-${field.id}`}>
        <button
          className="panda-bar-item flex-center hover-cursor panda-bar-item-reverse panda-bar-item-button"
          onMouseDown={(e) => this.handleMouseDown(selected.uid, y, e)}
          type="button"
        >
          <Icon icon={GLYPHS.DRAG_INDICATOR} size={{ height: 16, width: 16 }} />
        </button>
        <BarTextInput
          isReverse
          input={{
            name: 'fieldName',
            value: selected.displayName,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              updateTableColumnName({
                spaceId: space.id,
                colIdIndex: index,
                name: e.target.value,
              }),
          }}
          isFullWidth
          placeholder={field.name}
        />
        {attachmentId && (
          <BarIcon
            isSmall
            isReverse
            icon={GLYPHS.EDIT}
            onClick={() => addEditingAttachment(attachmentId)}
            iconSize={{ height: 12, width: 12 }}
          />
        )}
        {showDepthBtn && (
          <BarIcon
            isSmall
            isReverse
            icon={GLYPHS.ARROW_RIGHT}
            onClick={() => addIndexDepth(index)}
            iconSize={{ height: 12, width: 12 }}
          />
        )}
        {selectedRefTableRecs.length > 1 && (
          <BarIcon
            isSmall
            isReverse
            icon={GLYPHS.REMOVE}
            onClick={() => removeTableColumn({ spaceId: space.id, colIdIndex: index })}
            iconSize={{ height: 12, width: 12 }}
          />
        )}
      </Bar>
    );
  };
}

const mapDispatch = ({ space }: IDispatch) => ({
  addTableColumnByType: (payload: any) => space.addTableColumnByType(payload),
  updateTableColumnName: (payload: any) => space.updateTableColumnName(payload),
  updateTableColumnEntirety: (payload: any) => space.updateTableColumnEntirety(payload),
  removeTableColumn: (payload: any) => space.removeTableColumn(payload),
  addIndexDepth: (payload: any) => space.addIndexDepth(payload),
  addEditingAttachment: (payload: string) => space.addEditingAttachment(payload),
});

export default compose(
  injectIntl,
  connect(
    () => ({}),
    // @ts-ignore
    mapDispatch,
  ),
)(InputFieldSelection);
